/* eslint-disable react/forbid-prop-types */
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import { Axios } from "Helpers/utils";
import { useEffect, useState } from "react";
import { END } from "redux-saga";
import { loadSelf } from "@fdn/profile_store";
import Env from "Consts/env";
import * as ServerCookies from "cookies";
import dynamic from "next/dynamic";
import { setDataLayerNew } from "Helpers/dataLayer";
import {
	DATA_LAYER_SEARCH_RESULT_CLICK,
	FROM_SEARCH_DEFAULT,
	FROM_SEARCH_QUICK,
	VIEW_SEARCH_RESULT_PAGE,
} from "Consts/dataLayer";
import { wrapper } from "@/Store/index";

const Layout = dynamic(
	() => import("Containers/layout"),
	{ ssr: false },
);
const Mobile = dynamic(
	() => import("Components/pages/search/mobile"),
	{ ssr: false },
);
const Desktop = dynamic(
	() => import("Components/pages/search/desktop"),
	{ ssr: false },
);
const Popup = dynamic(
	() => import("Components/pdp-popup"),
	{ ssr: false },
);
const AddToCartPopup = dynamic(
	() => import("Components/add-to-cart-popup"),
	{ ssr: false },
);
const Drawer = dynamic(
	() => import("Components/drawer"),
	{ ssr: false },
);
const Loader = dynamic(
	() => import("@fdn/loader"),
	{ ssr: false },
);
const ModalQuickReview = dynamic(
	() => import("Components/modal-quick-review"),
	{ ssr: false },
);
const ChildDetailQuickReview = dynamic(
	() => import("Containers/child-detail-quick-review"),
	{ ssr: false },
);

const transformDataCategoryDesktop = (categoriesDesktop = []) => {

	if (!categoriesDesktop?.length) return [];

	return categoriesDesktop.map((category) => ({
		id: category.entity_id,
		name: category.category_name,
		slug: category.category_path,
		is_virtual: category.is_virtual || null,
		items: transformDataCategoryDesktop(category.children_data),
	}));

};

const SearchPage = ({
	query,
	isMobile,
	self,
	listCategoryDesktop,
	listCategory,
	listBrand,
	brandResult,
	extractParCat,
	extractBrand,
	isSearchProductFound,
	isBrandResultError,
	listSort,
}) => {

	const router = useRouter();

	const [productPagination, setProductPagination] = useState({ data: { products: [] } });
	const [isLoadingProducts, setIsLoadingProducts] = useState(true);
	const [isRecommendationError, setIsRecommendationError] = useState(false);
	const [isSearchResultError, setIsSearchResultError] = useState(false);

	const [isLoadingQuickReview, setIsLoadingQuickReview] = useState(false);
	const [productQuickReview, setProductQuickReview] = useState([]);
	const [isModalQuickReview, setIsModalQuickReview] = useState(false);

	const [addToCartLoading, setAddToCartLoading] = useState(false);
	const [addToCartPopupShow, setAddToCartPopupShow] = useState({
		show: false,
		addedProduct: {},
		productId: 0,
	});
	const [popupShow, setPopupShow] = useState({
		show: false,
		message: "",
		color: "#323942",
		redirect: false,
		closable: false,
	});

	const fetchRecommendationProduct = async () => {

		setIsLoadingProducts(true);
		setIsRecommendationError(false);
		try {

			const params = new URLSearchParams({ limit: isMobile ? 8 : 16 });
			const productsFetchResponse = await fetch(`/api/fds-search/result/recommendations?${params.toString()}`, {
				headers: { ...Env.HEADERS, key: process.env.NEXT_PUBLIC_KEY_API_CLIENT },
			});
			const productsResponse = await productsFetchResponse.json();

			if (!productsFetchResponse.ok) throw productsResponse;

			// TODO: Remove slice(8) after api can handle limit
			const productList = Array.isArray(productsResponse?.data?.results?.products)
				? productsResponse?.data?.results?.products?.slice(0, params.get("limit"))
				: [];
			const products = {
				products: productList ?? [],
				pagination: productsResponse?.pagination ?? {},
			};

			setProductPagination(products ?? { products: null, pagination: {} });

		} catch (error) {

			setIsRecommendationError(true);

		} finally {

			setIsLoadingProducts(false);

		}

	};

	const fetchSearchResult = async () => {

		try {

			setIsLoadingProducts(true);
			setIsRecommendationError(false);
			setIsSearchResultError(false);

			const queryParams = {
				page: router.query.page ?? 1,
				limit: isMobile ? 8 : 16,
				category: router.query.category ?? "",
				brand: router.query.brand ?? "",
				min_price: router.query.min_price ?? "",
				max_price: router.query.max_price ?? "",
				sale: router.query.sale ?? "",
				query: router.query.query ?? "",
				placement: router.query.query?.length > 0 ? 3 : 2,
			};
			const searchParams = new URLSearchParams(queryParams);
			const productsFetchResponse = await fetch(`/api/fds-search/result/products?${searchParams.toString()}`);
			const productsResponse = await productsFetchResponse.json();

			if (!productsFetchResponse.ok) throw productsResponse;

			if (!isSearchProductFound) {

				await fetchRecommendationProduct();
				return;

			}

			const products = {
				products: productsResponse?.data?.results?.products,
				pagination: productsResponse?.pagination,
			};

			setProductPagination(products ?? { products: null, pagination: {} });

		} catch (error) {

			setIsSearchResultError(true);

		} finally {

			setIsLoadingProducts(false);

		}

	};

	const handleQuickReview = async (params) => {

		try {

			setIsModalQuickReview(true);
			setIsLoadingQuickReview(true);
			const resProduct = await Axios(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-product/${params}`);

			if (resProduct?.data?.meta?.code === 200) {

				setProductQuickReview(resProduct?.data?.data?.product);
				setIsLoadingQuickReview(false);

			}

		} catch (err) {

			setPopupShow({
				show: true,
				message: "Something went wrong. Please try again later.",
				color: "#323942",
			});

		}

	};

	const handleDataLayer = (value, section) => {

		setDataLayerNew()?.search({
			event: DATA_LAYER_SEARCH_RESULT_CLICK,
			username: self.data?.username || "guest",
			is_login: !!self.data?.username,
			device: isMobile ? "mobile" : "desktop",
			keyword: query.query,
			section,
			data: value,
		});

	};

	useEffect(() => {

		const { ref } = router.query;
		const navbarRef = ref === "default" ? FROM_SEARCH_DEFAULT : FROM_SEARCH_QUICK;

		setDataLayerNew()?.view({
			event: VIEW_SEARCH_RESULT_PAGE,
			username: self.data?.username || "guest",
			is_login: !!self.data?.username,
			device: isMobile ? "mobile" : "desktop",
			referral: ref ? navbarRef : FROM_SEARCH_QUICK,
		});

		fetchSearchResult();

	}, [router.query]);

	const staticMeta = query?.query ?? "";

	const defaultCategoryDesktop = listCategoryDesktop?.filter((item) => item.slug === "shop-all")[0]?.items;

	return (
		<Layout
			home
			title="Femaledaily"
			titleNavbar="Search"
			typeNavbarSecondary="type-1"
			ismobile={isMobile}
			device={query?.device}
			self={self}
			categoryDesktop={listCategoryDesktop}
			getQuoteIdErrMessage={null}
			metaTitle={`Jual Produk ${staticMeta} Terlengkap dan Berkualitas`}
			metaDescription={`Jual Produk ${staticMeta} Berkualitas. Belanja Makeup Online Terpercaya di FD Studio by Female Daily`}
			isAdaptiveLoad
			isShowSecondaryNav={false}
		>
			{isMobile
				? (
					<Mobile
						products={productPagination?.products ?? []}
						isProductLoading={isLoadingProducts}
						isProductSearchFound={isSearchProductFound}
						isRecommendationError={isRecommendationError}
						isSearchResultError={isSearchResultError}
						brands={brandResult}
						isBrandLoading={false}
						isErrorBrand={isBrandResultError}
						page={productPagination?.pagination?.page}
						total={productPagination?.pagination?.total}
						totalPage={productPagination?.pagination?.total_page}
						handleRefetch={fetchSearchResult}
						extractBrand={extractBrand}
						listFilterCategory={listCategory}
						listFilterBrand={listBrand}
						listSort={listSort}
						handleDataLayer={handleDataLayer}
					/>
				)
				: (
					<Desktop
						products={productPagination?.products ?? []}
						isProductLoading={isLoadingProducts}
						isProductSearchFound={isSearchProductFound}
						isRecommendationError={isRecommendationError}
						isSearchResultError={isSearchResultError}
						brands={brandResult}
						isBrandLoading={false}
						isErrorBrand={isBrandResultError}
						page={productPagination?.pagination?.page}
						total={productPagination?.pagination?.total}
						totalPage={productPagination?.pagination?.total_page}
						extractBrand={extractBrand}
						listFilterBrand={listBrand}
						listFilterCategory={defaultCategoryDesktop}
						handleQuickReview={handleQuickReview}
						extractParCat={extractParCat}
						popupShow={popupShow}
						setPopupShow={setPopupShow}
						setAddToCartLoading={(val) => setAddToCartLoading(val)}
						setAddToCartPopupShow={(val) => setAddToCartPopupShow(val)}
						listSort={listSort}
						handleDataLayer={handleDataLayer}
					/>
				)}

			<Popup
				closable={false}
				isMobile={isMobile}
				color={popupShow?.color}
				show={popupShow?.show}
				onHide={() => setPopupShow({ ...popupShow, show: false })}
				text={popupShow?.message}
				withOk={false}
			/>

			<ModalQuickReview
				show={isModalQuickReview}
				onClick={() => setIsModalQuickReview(false)}
				closable
			>
				<ChildDetailQuickReview
					data={productQuickReview}
					isLoading={isLoadingQuickReview}
					closeModal={() => setIsModalQuickReview(false)}
					setPopupShow={(e) => setPopupShow(e)}
					setAddToCartLoading={(e) => setAddToCartLoading(e)}
					setAddToCartPopupShow={(e) => setAddToCartPopupShow(e)}
				/>
			</ModalQuickReview>

			<Drawer
				isMobile={false}
				contentBackground="#FFF"
				title="Added to Cart!"
				headerIconSrc="https://s3.ap-southeast-1.amazonaws.com/assets.femaledaily.com/fdn-studio-new/static/added-to-cart.png"
				onHide={() => setAddToCartPopupShow((prev) => ({
					show: false,
					addedProduct: prev.addedProduct,
					productId: prev.productId,
				}))}
				type="with-overlay"
				show={addToCartPopupShow?.show}
			>
				<AddToCartPopup
					isMobile={false}
					addedProduct={addToCartPopupShow?.addedProduct}
					productId={addToCartPopupShow?.productId}
				/>
			</Drawer>

			{addToCartLoading && (
				<>
					<div className="fdn-loader">
						<Loader />
					</div>
					<style>
						{`
							.fdn-loader {
								position:absolute;
							}
						`}
					</style>
				</>
			)}

		</Layout>
	);

};

SearchPage.propTypes = {
	query: PropTypes.any.isRequired,
	isMobile: PropTypes.bool.isRequired,
	self: PropTypes.any.isRequired,
	listCategoryDesktop: PropTypes.array.isRequired,
	listCategory: PropTypes.array.isRequired,
	listBrand: PropTypes.array.isRequired,
	extractParCat: PropTypes.any.isRequired,
	extractBrand: PropTypes.any.isRequired,
	brandResult: PropTypes.array.isRequired,
	isBrandResultError: PropTypes.bool.isRequired,
	isSearchProductFound: PropTypes.bool.isRequired,
	listSort: PropTypes.array.isRequired,
};

export const getServerSideProps = wrapper.getServerSideProps(
	async ({
		store, req, res, query,
	}) => {

		let userAgent;
		let authorization = null;
		let usrname = null;
		let referer = null;

		if (req) {

			if (req?.headers?.referer) {

				referer = req.headers?.referer;

			}

			// if you are on the server and you get a "req" property from your context
			userAgent = req.headers["user-agent"]; // get the user-agent from the headers

		} else {

			// if you are on the client you can access the navigator from the window object
			userAgent = navigator.userAgent;

		}

		const isMobile = Boolean(
			userAgent.match(
				/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i,
			),
		);

		const Cookie = new ServerCookies(req, res);

		if (req.headers.cookie) {

			authorization = Cookie.get("token");
			usrname = Cookie.get("usrname");

		}

		await store.dispatch(
			loadSelf({
				payload: {
					api_env: process.env.NEXT_PUBLIC_NODE_ENV,
					env: Env.HEADERS,
					usrname,
					Authorization: authorization,
					is_server: true,
				},
			}),
		);

		store.dispatch(END);
		await store.sagaTask.toPromise();

		const self = store.getState().storeSelf && typeof store.getState().storeSelf.data !== "undefined"
			? store.getState().storeSelf
			: null;

		const categoryDesktopFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-category/2`, {
			headers: { ...Env.HEADERS },
		});
		const categoryDesktopResponse = await categoryDesktopFetchResponse.json();
		const cleanCategoryDesktop = categoryDesktopResponse?.data?.[0]?.children_data?.filter((val) => val.include_in_menu === "1");
		const listCategoryDesktop = transformDataCategoryDesktop(cleanCategoryDesktop);

		const categoriesFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-category/3`, {
			headers: { ...Env.HEADERS },
		});
		const categoriesResponse = await categoriesFetchResponse.json();
		const listCategory = categoriesResponse?.data?.[0]?.children_data?.map((item) => ({
			id: item.entity_id,
			name: item.category_name,
			slug: item.category_slug,
			children_data: item.children_data,
		})) ?? [];

		const brandSearchParams = new URLSearchParams({ limit: 1000, sort: "name" });
		const brandsFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-brands?${brandSearchParams.toString()}`, { headers: { ...Env.HEADERS } });
		const brandsResponse = await brandsFetchResponse.json();
		const listBrand = brandsResponse?.data ?? [];

		listCategoryDesktop.splice(1, 0, {
			id: 200, name: "Brands", slug: "brands", items: [],
		});

		const extractParCat = {};
		const extractSubCat = {};

		listCategory.forEach((subcat) => {

			extractParCat[subcat.slug] = subcat.id;
			subcat.children_data?.forEach((subchild) => {

				extractSubCat[subchild.category_slug] = subchild.entity_id;

			});

		});

		const extractBrand = listBrand?.reduce((accumulative, brand) => ({
			...accumulative,
			[brand.brand_id]: brand.brand_name,
		}), {});

		const searchParams = new URLSearchParams({ limit: 1, query: query.query ?? "", placement: query.query?.length > 0 ? 3 : 2 });
		const productsFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-search/result/products?${searchParams.toString()}`, { headers: { ...Env.HEADERS, key: process.env.NEXT_PUBLIC_KEY_API_CLIENT } });
		const productsResponse = await productsFetchResponse.json();
		const isSearchProductFound = Array.isArray(productsResponse?.data?.results?.products)
			&& productsResponse?.data?.results?.products?.length > 0;

		let isBrandResultError = false;
		let brandResult = [];
		try {

			const brandQueryParams = new URLSearchParams({ limit: 10, query: query.query ?? "", placement: query.query?.length > 0 ? 3 : 2 });
			const brandResultFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-search/result/brands?${brandQueryParams.toString()}`, { headers: { ...Env.HEADERS, key: process.env.NEXT_PUBLIC_KEY_API_CLIENT } });
			const brandResultResponse = await brandResultFetchResponse.json();
			if (!brandResultFetchResponse.ok) throw brandResultResponse;
			brandResult = brandResultResponse?.data?.brands ?? [];

		} catch (error) {

			isBrandResultError = true;

		}

		const sortFetchResponse = await fetch(`${process.env.NEXT_PUBLIC_ROOT_DOMAIN}/api/fds-sort`);
		const sortResponse = await sortFetchResponse.json();
		const listSort = sortResponse?.data?.results?.map((val) => ({
			name: val.name,
			id: val.value,
		})) ?? [];

		return {
			props: {
				query,
				isMobile,
				self,
				listCategoryDesktop,
				listCategory,
				listBrand,
				extractParCat,
				extractBrand,
				brandResult,
				isSearchProductFound,
				isBrandResultError,
				listSort,
				referer,
			}, // will be passed to the page component as props
		};

	},
);

export default SearchPage;
